import { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import './App.css';

const stripePromise = loadStripe('pk_live_51RADxoKb4y4i6za2zfsd2f5RPbchgmeObQEdOxyJpQTIYZkXoWKvIlHwmlTisJRt5gCcVgOG4UMRcGN0SmJ32qf000OLWXKHJn');

const BUNDLE_PRICE_ID = 'price_1TYWsAKb4y4i6za2LgMLdDbg';

const PRODUCTS = [
  {
    id: 'invisible',
    emoji: '💜',
    title: 'Invisible-to-Booked AI System',
    tagline: 'Your complete conversation system in 24 hours. Copy, paste, done.',
    deliverables: [
      'Plug-and-play prompt client sequences',
      '"Cold-to-warm" DM script PDF (editable)',
      '7-day lead calendar; post + DM plan',
    ],
    promise: 'Post. Engage. Sale. Today.',
  },
  {
    id: 'softreset',
    emoji: '🌿',
    title: 'The Soft Reset System',
    tagline: 'Rebuild your energy, confidence, and clarity. In 7 quiet days.',
    deliverables: [
      '7-day guided journal PDF',
      '21 anytime self-reflection prompts',
      '"Soft Morning" audio affirmation script',
      'Energy audit worksheet + wallpaper pack',
    ],
    promise: 'In 7 days: more clarity, less noise, a version of you that wants to show up.',
  },
  {
    id: 'identity',
    emoji: '✨',
    title: 'The Identity Upgrade Kit',
    tagline: 'Stop playing small online. Rewrite who you are with AI assistance.',
    deliverables: [
      '"Who Am I Really": 10 deep prompts',
      'IG bio rewrite template (3 formulas)',
      'Offer clarity worksheet',
      '5 caption frameworks + affirmation card deck',
    ],
    promise: "A bio that stops people cold, an offer you're proud of, a voice that owns the room.",
  },
];

function LandingPageFlowSystem() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [showCheckout, setShowCheckout] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);

  const checkoutRef = useRef(null);
  const pricingRef = useRef(null);

  useEffect(() => {
    if (showCheckout && clientSecret && checkoutRef.current) {
      checkoutRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCheckout, clientSecret]);

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://us-west4-smartincomeig-eb2af.cloudfunctions.net/createcheckoutsession', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: BUNDLE_PRICE_ID,
          successUrl: `${window.location.origin}/thank-you?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/`,
        }),
      });
      const { clientSecret } = await response.json();
      setShowCheckout(true);
      setClientSecret(clientSecret);
    } catch (err) {
      console.error('Checkout failed:', err);
      alert('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="noise" />

      <nav className="nav">
        <span className="nav-logo">@AskSophiaLee</span>
        <button className="nav-cta" onClick={scrollToPricing}>Get The Flow System for $17</button>
      </nav>

      <section className="hero">
        <span className="hero-eyebrow">The Flow System — Invisible-to-Booked 7-Day AI System</span>
        <h1 className="hero-headline">
          They called it a hobby.<br />
          <span className="gradient-text">The Flow System ends that.</span>
        </h1>
        <p className="hero-sub">
          Copy. Paste. Done. Zero tech skills or past AI experience required. Just plug in the prompts and watch it run. In 20 minutes you'll have something working. In 90 days you'll wonder why you waited.
        </p>
        <button className="btn-primary" onClick={scrollToPricing}>
          Claim The Flow System for $17 →
        </button>
      </section>

      <div className="proof-strip">
        {[
          "I'm not a tech person but I had it running in 20 minutes",
          "I've tried ChatGPT before and this is the first time it actually did something",
          "Felt stupid simple. That's why it worked.",
        ].map((t, i) => (
          <span key={i} className="proof-pill">{t}</span>
        ))}
      </div>

      <section className="section" id="products">
        <div className="section-label">What's inside The Flow System</div>
        <h2 className="section-title">3 modules. Every problem solved.</h2>
        <p className="section-sub">No tech skills. No learning curve. No guessing. Less posting. More closing. The system doesn't care how small your audience is — it works anyway.</p>

        <div className="product-grid">
          {PRODUCTS.map((p) => (
            <div className="product-card" key={p.id}>
              <span className="product-emoji">{p.emoji}</span>
              <h3 className="product-card-title">{p.title}</h3>
              <p className="product-tagline">{p.tagline}</p>
              <ul className="product-deliverables">
                {p.deliverables.map((d, i) => (
                  <li key={i}><span>✓</span>{d}</li>
                ))}
              </ul>
              <p className="product-promise">"{p.promise}"</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-dark">
        <div className="section-label">The path</div>
        <h2 className="section-title">From zero to earning in days</h2>

        <div className="steps">
          {[
            { n: '01', title: 'Grab The Flow System', desc: "Download everything instantly. It's ready in seconds. Zero setup time." },
            { n: '02', title: 'Open your preferred AI', desc: 'Paste the included prompts. Within the hour you\'ll have your first prompt running.' },
            { n: '03', title: 'Post & DM', desc: 'Follow the roadmap. Post once. DM five people. Close one sale. It worked for women who\'ve never touched AI.' },
            { n: '04', title: 'Repeat & scale', desc: 'Within the year you won\'t recognize your income. Each module builds on the last. Your income compounds.' },
          ].map((s, i) => (
            <div className="step" key={i}>
              <span className="step-num">{s.n}</span>
              <div>
                <h4 className="step-title">{s.title}</h4>
                <p className="step-desc">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section pricing-section" id="pricing" ref={pricingRef}>
        <div className="section-label">Get The Flow System</div>
        <h2 className="section-title">Start today.</h2>
        <p className="section-sub">All 3 modules + 1 Bonus Claude Roadmap for Beginners for $17. One decision. Instant delivery.</p>

        <div className="checkout-wrapper">
          <div className="order-summary">
            <div className="order-line">
              <span>The Flow System (All 3 Modules)</span>
              <span className="order-price">$17</span>
            </div>
            <div className="order-line">
              <span>+ Bonus: Claude Roadmap for Beginners</span>
              <span className="order-price">Free</span>
            </div>
            <div className="order-savings"><span>Bundle discount</span><span>Save $4</span></div>
            <div className="order-total">
              <span>Total</span>
              <span>$17</span>
            </div>

            <div className="risk-stack">
              <div className="risk-row">
                <span className="risk-num">01</span>
                <div>
                  <strong>Instant delivery. Zero setup.</strong>
                  <p>Everything is ready the moment you pay. Copy and paste — immediate value. No waiting, no onboarding, no friction.</p>
                </div>
              </div>
              <div className="risk-row">
                <span className="risk-num">02</span>
                <div>
                  <strong>No long-term commitments.</strong>
                  <p>No sneaky recurring monthly subscription fees. $17 once. That's it.</p>
                </div>
              </div>
              <div className="risk-row">
                <span className="risk-num">03</span>
                <div>
                  <strong>The Sophia Pinky Promise — The 7-Day Grace Period</strong>
                  <p>Take 7 full days to play with the prompts. If it's not a perfect match, enjoy a no-drama refund (minus standard checkout processing fees).</p>
                </div>
              </div>
            </div>

            <button
              ref={checkoutRef}
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get The Flow System — $17 →'}
            </button>
            <p className="checkout-fine">Stripe handles payment securely. Instant download. No subscriptions.</p>
          </div>

          {showCheckout && clientSecret && (
            <div className="embedded-checkout">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>
      </section>

      <section className="section finale">
        <h2 className="finale-title">
          The only question is:<br />
          <span className="gradient-text">what are you waiting for?</span>
        </h2>
        <p className="finale-sub">One decision. $17. A completely different trajectory.</p>
        <button className="btn-primary btn-xl" onClick={scrollToPricing}>Yes, I want The Flow System for $17</button>
      </section>

      <footer className="footer">
        <span>© 2026 @AskSophiaLee</span>
        <span>Results vary. Income depends on effort and execution.</span>
      </footer>

      <div className="sticky-cta">
        <button onClick={scrollToPricing}>Get The Flow System for $17 →</button>
      </div>
    </div>
  );
}

export default LandingPageFlowSystem;